import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Edit3,
  Trash2,
  FileText,
  Mail,
  Phone,
  MapPin,
  User,
  Building2,
  CreditCard,
  Calendar,
  Plus,
  Download,
  Eye
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { clientService, contractService, serviceService, templateService } from '../../services';
import { ContractForm } from '../../components/forms';
import { ClientForm } from '../../components/forms';

const ClientDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showContractForm, setShowContractForm] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);

  // Fetch client data
  const { data: client, isLoading, error } = useQuery(
    ['client', id],
    () => clientService.getById(id),
    {
      enabled: !!id,
      onError: (error) => {
        toast.error('Eroare la încărcarea datelor clientului');
        navigate('/clients');
      }
    }
  );

  // Fetch client contracts
  const { data: contracts = [] } = useQuery(
    ['client-contracts', id],
    () => contractService.getContractsByClient(id),
    {
      enabled: !!id
    }
  );

  // Fetch services for contract form
  const { data: services = [] } = useQuery('services', serviceService.getAll, {
    onError: (error) => {
      console.error('Failed to load services:', error);
    }
  });

  // Fetch templates for contract form
  const { data: templates = [] } = useQuery('templates', templateService.getActive, {
    onError: (error) => {
      console.error('Failed to load templates:', error);
    }
  });

  // Delete mutation
  const deleteMutation = useMutation(
    () => clientService.deleteClient(id),
    {
      onSuccess: () => {
        toast.success('Client șters cu succes');
        queryClient.invalidateQueries('clients');
        navigate('/clients');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || 'Eroare la ștergerea clientului');
        setShowDeleteModal(false);
      }
    }
  );

  // Update client mutation
  const updateClientMutation = useMutation(
    (clientData) => clientService.update(id, clientData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client', id]);
        queryClient.invalidateQueries('clients');
        toast.success('Client actualizat cu succes');
        setShowClientForm(false);
      },
      onError: (error) => {
        console.error('Failed to update client:', error);
        toast.error('Eroare la actualizarea clientului');
      }
    }
  );

  // Create contract mutation
  const createContractMutation = useMutation(
    contractService.create,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['client-contracts', id]);
        queryClient.invalidateQueries('contracts');
        toast.success('Contract creat cu succes');
        setShowContractForm(false);
      },
      onError: (error) => {
        console.error('Failed to create contract:', error);
        toast.error('Eroare la crearea contractului');
      }
    }
  );

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const handleClientSubmit = async (clientData) => {
    try {
      await updateClientMutation.mutateAsync(clientData);
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  const handleContractSubmit = async (contractData) => {
    try {
      // Pre-fill clientId with current client
      const dataWithClient = {
        ...contractData,
        clientId: parseInt(id)
      };
      await createContractMutation.mutateAsync(dataWithClient);
    } catch (error) {
      // Error handled in mutation callbacks
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse">
            <div className="h-8 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="h-6 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Client nu a fost găsit</h2>
          <p className="text-gray-600 mb-4">Clientul solicitat nu există sau a fost șters.</p>
          <Link
            to="/clients"
            className="inline-flex items-center px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Înapoi la clienți
          </Link>
        </div>
      </div>
    );
  }

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Generated': 'bg-blue-100 text-blue-800',
      'Sent': 'bg-yellow-100 text-yellow-800',
      'Accessed': 'bg-orange-100 text-orange-800',
      'Signed': 'bg-green-100 text-green-800',
      'Expired': 'bg-red-100 text-red-800',
      'Terminated': 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/clients')}
                className="inline-flex items-center text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="h-5 w-5 mr-1" />
                Înapoi la clienți
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{client?.name}</h1>
                <p className="text-gray-600">Cod fiscal: {client?.fiscalCode}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setShowClientForm(true)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-colors"
              >
                <Edit3 className="h-4 w-4 mr-2" />
                Editează
              </button>
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center px-4 py-2 border border-red-300 rounded-lg text-red-700 bg-white hover:bg-red-50 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Șterge
              </button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Client Information */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Informații client</h2>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Info */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Denumire</p>
                        <p className="text-gray-900">{client?.name}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Cod fiscal</p>
                        <p className="text-gray-900">{client?.fiscalCode}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <a href={`mailto:${client?.email}`} className="text-primary-600 hover:text-primary-500">
                          {client?.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Telefon</p>
                        <p className="text-gray-900">{client?.phone}</p>
                      </div>
                    </div>

                    <div className="flex items-center space-x-3">
                      <MapPin className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Adresă</p>
                        <p className="text-gray-900">{client?.fullAddress}</p>
                      </div>
                    </div>
                  </div>

                  {/* Financial Info */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <CreditCard className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">IBAN</p>
                        <p className="text-gray-900 font-mono text-sm">{client?.iban}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Bancă</p>
                        <p className="text-gray-900">{client?.bank}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Data înregistrării</p>
                        <p className="text-gray-900">
                          {new Date(client?.createdAt).toLocaleDateString('ro-RO')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Representative Info */}
                <div className="mt-6 pt-6 border-t border-gray-200">
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Reprezentant legal</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center space-x-3">
                      <User className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Nume</p>
                        <p className="text-gray-900">{client?.representativeFirstName} {client?.representativeName}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Building2 className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Funcție</p>
                        <p className="text-gray-900">{client?.representativePosition}</p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Mail className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Email</p>
                        <a href={`mailto:${client?.representativeEmail}`} className="text-primary-600 hover:text-primary-500">
                          {client?.representativeEmail}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-center space-x-3">
                      <Phone className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="text-sm font-medium text-gray-500">Telefon</p>
                        <p className="text-gray-900">{client?.representativePhone}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Statistici</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Contracte totale</span>
                  <span className="font-semibold text-gray-900">{contracts.length}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Contracte active</span>
                  <span className="font-semibold text-green-600">
                    {contracts.filter(c => c.status === 'Signed').length}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">În așteptare</span>
                  <span className="font-semibold text-yellow-600">
                    {contracts.filter(c => ['Sent', 'Accessed'].includes(c.status)).length}
                  </span>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Acțiuni rapide</h3>
              <div className="space-y-3">
                <button
                  onClick={() => setShowContractForm(true)}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Contract nou
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Contracts Section */}
        <div className="mt-8">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-gray-900">
                  Contracte ({contracts.length})
                </h2>
                <button
                  onClick={() => setShowContractForm(true)}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 transition-colors"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Nou
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              {contracts.length === 0 ? (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Niciun contract</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Acest client nu are contracte create încă.
                  </p>
                  <div className="mt-6">
                    <button
                      onClick={() => setShowContractForm(true)}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-primary-500 hover:bg-primary-600 transition-colors"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Creează primul contract
                    </button>
                  </div>
                </div>
              ) : (
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Număr contract
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data început
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Data expirare
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Servicii
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acțiuni
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {contracts.map((contract) => (
                      <tr key={contract.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {contract.number}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                            {contract.statusText}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(contract.startDate).toLocaleDateString('ro-RO')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(contract.dueDate).toLocaleDateString('ro-RO')}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {contract.servicesCount} servicii
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Link
                            to={`/contracts/${contract.id}`}
                            className="text-primary-600 hover:text-primary-900"
                          >
                            <Eye className="h-4 w-4 inline" />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full flex items-center justify-center z-50">
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <Trash2 className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 text-center mb-2">
                Șterge client
              </h3>
              <p className="text-sm text-gray-500 text-center mb-6">
                Sunteți sigur că doriți să ștergeți clientul <strong>{client?.name}</strong>?
                Această acțiune nu poate fi anulată.
              </p>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={deleteMutation.isLoading}
                >
                  Anulează
                </button>
                <button
                  onClick={handleDelete}
                  disabled={deleteMutation.isLoading}
                  className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleteMutation.isLoading ? 'Se șterge...' : 'Șterge'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Client Form Modal */}
      <ClientForm
        client={client}
        isOpen={showClientForm}
        onClose={() => setShowClientForm(false)}
        onSubmit={handleClientSubmit}
        loading={updateClientMutation.isLoading}
      />

      {/* Contract Form Modal */}
      <ContractForm
        contract={null}
        clients={[client]} // Pass only current client
        services={services}
        templates={templates}
        isOpen={showContractForm}
        onClose={() => setShowContractForm(false)}
        onSubmit={handleContractSubmit}
        loading={createContractMutation.isLoading}
        preselectedClientId={id} // Pass the client ID to pre-select
      />
    </div>
  );
};

export default ClientDetails;